import React, { useRef } from "react";
import MapComponent from './MapComponent';
import Sidebar from '../Components/Sidebar';
import { useState, useEffect } from 'react';
import { schoolPoleLocationApi } from '../services/api';
import { getEmail } from '../services/authenticationService';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';

const ViewerDashboard: React.FC = () => {
    const mapRef = useRef(null);

    const [schools, setSchools] = useState({});
    const [defaultCenter, setDefaultCenter] = useState({ lat: 0, lng: 0 });
    const [poles, setPoles] = useState([]);
    const [zoom, setZoom] = useState(11);
    const [allPoles, setAllPoles] = useState([]);
    const [priorities, setPriorities] = useState({ High: true, Medium: true, Low: true, PoleAction: true });
    const [poleid, setPoleid] = useState(null);
    const [resetData, setResetData] = useState(false);
    const [map, setMap] = useState({ setZoom: z => console.log(z) });
    const [selectedSchool, setSelectedSchool] = useState('');
    const [isImagesLoading, setIsImagesLoading] = useState(false);

    useEffect(() => {
        getSchoolList();
    }, []);

    const filterPolesByPriorities = (polesArr) => {
        return [...polesArr].map(pole => {
            if (pole.high > 0)
                pole.visible = priorities.High;
            else if (pole.medium > 0)
                pole.visible = priorities.Medium;
            else if (pole.low > 0)
                pole.visible = priorities.Low;

            if (pole.comment > 0)
                pole.visible = priorities.PoleAction;
            return pole;
        });
    };

    // toggle visible variable of each pole according to priorities and setPoles
    const applyPriorities = () => {
        setPoles(filterPolesByPriorities(poles));
        resetPoleData();  //To reset pole data
    };

    // call applyPriorities when priorities get changed.
    useEffect(applyPriorities, [priorities]);

    const getSchoolList = () => {
        schoolPoleLocationApi({ email: getEmail(), listOfSchoolIds: [] }).then(response => {  //["39","44","2","308","323","397"] add this array at time of qa testing
            const schoolGroups = groupBySchool(response);
            setSchools(schoolGroups);
            const { latitude, longitude } = response[0];
            setCenter({ latitude, longitude });
            // set schools as markers
            setPoles(getSchoolMarkers(schoolGroups));
            setAllPoles(response);
        }).catch(err => {
            toast.error(err.toString());
        });
    };

    const refreshMap = () => {
        const { latitude: lat, longitude: lng } = allPoles[0];
        goToSchools({ lat, lng });
        map.setZoom(12);
        resetPoleData();
        setSelectedSchool('');
    };

    const getSchoolMarkers = (schoolGroups) => {
        return Object.keys(schoolGroups).map(key => {
            const issues = schoolGroups[key].reduce((acc, pole) => {
                return {
                    high: acc.high + (pole.high > 0 ? 1 : 0),
                    medium: acc.medium + (!pole.high && pole.medium > 0 ? 1 : 0),
                    low: acc.low + (!pole.high && !pole.medium && pole.low > 0 ? 1 : 0)
                }
            }, { high: 0, medium: 0, low: 0 });
            return { isSchool: true, ...schoolGroups[key][0], ...issues };
        });
    };

    const goToSchools = (center) => {
        // set schools as markers
        const schoolMarkers = getSchoolMarkers(schools);
        setZoom(12);
        setDefaultCenter(center);
        setPoles(schoolMarkers);
    };

    const goToPoles = (center) => {
        setZoom(17);
        setDefaultCenter(center);
        // first filter poles by priorities then set poles
        setPoles(filterPolesByPriorities(allPoles));
    };

    const groupBySchool = (array) => {
        const hash = {};
        array.forEach(element => {
            if (hash[element.school_Name]) {
                hash[element.school_Name].push(element);
            } else {
                hash[element.school_Name] = [element];
            }
        });
        return hash;
    };

    const scrollToSchool = (elementId) => {
        const el = document.getElementById(elementId);
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    };

    const setCenter = (hash, zoomValue = 12) => {
        resetPoleData();
        setDefaultCenter({ lat: parseFloat(hash.latitude), lng: parseFloat(hash.longitude) });
        setZoom(zoomValue);
        if (hash.school_Name) {
            setPoles(filterPolesByPriorities(schools[hash.school_Name]));
            setSelectedSchool(hash.school_Name);
            scrollToSchool(hash.school_Name);
        }
    };

    const markerClickHandler = (pole) => {
        if (pole.isSchool)
            setCenter(schools[pole.school_Name][0], 17);
    };

    const showInfoTab = id => setPoleid(id); //To navigate to information tab
    const resetPoleData = () => setResetData(true);  //Reset pole information and images
    const resetPoleDataOne = () => setResetData(false);

    const updatePoleData = (pole_data) => {
        const pole = poles.find(p => p.pole_Id === pole_data.poleId);
        if (pole) {
            pole.high = pole_data.high;
            pole.medium = pole_data.medium;
            pole.low = pole_data.low;
            pole.comment = pole_data.comment;
            setPoles([...poles]); //clone array and update pole
        }
    };

    const updateFilteredSchools = (schools) => {
        setPoles(getSchoolMarkers(schools));
    };

    const getMapImage = () => {
        return new Promise((resolve, reject) => {
            if (map) {
                const listener = google.maps.event.addListener(map, 'tilesloaded', () => {
                    setTimeout(() => {
                        html2canvas(mapRef.current, { useCORS: true, scale: 1.2 }).then((canvas) => {
                            const dataUrl = canvas.toDataURL();
                            resolve({
                                url: dataUrl,
                                width: mapRef.current.clientWidth,
                                height: mapRef.current.clientHeight
                            });
                            google.maps.event.removeListener(listener);
                        }).catch(error => reject(error));
                    },500);
                });
            } else {
                reject(new Error('mapRef is not available'));
            }
        });
    };

    const controlImageCreation  = (flag) => {
        setIsImagesLoading(flag);
    };

    return (
        <div className="position-relative">
            <Sidebar
                schools={schools}
                poleid={poleid}
                priorities={priorities}
                setCenter={setCenter}
                setPriorities={setPriorities}
                refreshMap={refreshMap}
                resetPoleDataOne={resetPoleDataOne}
                selectedSchool={selectedSchool}
                updatePoleData={updatePoleData}
                updateFilteredSchools={updateFilteredSchools}
                getMapImage={getMapImage}
                controlImageCreation ={controlImageCreation }
                map={map}
            />
            {poles.length > 0 ?
            <div ref={mapRef}>
                <MapComponent
                    defaultCenter={defaultCenter}
                    poles={poles}
                    allPoles={allPoles}
                    defaultZoom={zoom}
                    priorities={priorities}
                    markerClickHandler={markerClickHandler}
                    goToSchools={goToSchools}
                    goToPoles={goToPoles}
                    showInfoTab={showInfoTab}
                    setCenter={setCenter}
                    resetPoleData={resetPoleData}
                    resetData={resetData}
                    setMap={setMap}
                    selectedPoleId={poleid}
                    isImagesLoading={isImagesLoading}
                    getMapImage={getMapImage}
                    controlImageCreation ={controlImageCreation }
                />
                </div> : null
            }
        </div>
    );
};

export default ViewerDashboard;